import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { emailFormatValidator, searchErrorHandler } from "utils/formValidation";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import Datepicker from "components/Input/Datepicker/Datepicker";
import Selection from "components/Input/Selection/Selection";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import ChangeContent from "./components/ChangeContent/ChangeContent";
import CreateContent from "./components/CreateContent/CreateContent";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import UpdateDataIcon from "assets/updateData.svg";
import s from "./SalesPerformance.module.scss";
import { changeSales, createSales, queryList, updateCommission } from "./model";

const SalesPerformance = ({ path }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "salesPerformanceGrid";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    startDate: null,
    endDate: null,
    currency: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
  });

  const [columnsArray, setColumnsArray] = useState([]);
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);
  const [changeDialogData, setChangeDialogData] = useState({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDialogData, setCreateDialogData] = useState({});

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse === undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const initialFetch = async () => {
    setLoading(true);
    const response = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      startDate: null,
      endDate: null,
      currency: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const response = await queryList(payload);
    const result = response?.data;
    if (result?.message === "Unauthorized") {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    } else if (result?.responseCode !== "0000") {
      snackbar(
        t("general.message.search.error") + ": " + result?.message,
        "error",
      );
    } else if (result?.responseCode === "0000") {
      setDatasource(result);
      setCurrentPage(result.page);
      setPageSize(result.size);
      pageSizeInfoRef.current.currentpage = result.page;
      pageSizeInfoRef.current.pagesize = result.size;
    }
    setSearch(true);
    setSearching(false);
  };

  // ===================== change funciton ===================== //
  const openChangeDialog = (data) => {
    setChangeDialogData(data);
    setIsChangeDialogOpen(true);
  };

  const closeChangeDialog = () => {
    restoreChangeFormInput();
    setIsChangeDialogOpen(false);
  };

  const handleChangeFormInput = (data, field) => {
    setChangeDialogData({
      ...changeDialogData,
      [field]: data,
    });
  };

  const restoreChangeFormInput = () => {
    setChangeDialogData({});
  };

  const changeContent = () => {
    return (
      <ChangeContent
        handleInput={handleChangeFormInput}
        data={changeDialogData}
      />
    );
  };

  const handleChangeSales = async () => {
    const payload = trimObject({
      id: changeDialogData.id,
      firstName: changeDialogData.firstName,
      lastName: changeDialogData.lastName,
      email: changeDialogData.email,
      status: changeDialogData.status,
    });
    const response = await changeSales(payload);
    const result = response?.data;
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.change.function.success.message"), "success");
      return true;
    } else {
      snackbar(t("general.message.change.function.fail.message"), "error");
      return false;
    }
  };

  const confirmChange = async () => {
    if (!changeDialogData.firstName) {
      snackbar(
        t("page.analytics.sales.performance.change.function.error.message.1"),
        "warning",
      );
      return;
    }
    if (!changeDialogData.lastName) {
      snackbar(
        t("page.analytics.sales.performance.change.function.error.message.2"),
        "warning",
      );
      return;
    }
    if (!changeDialogData.email) {
      snackbar(
        t("page.analytics.sales.performance.change.function.error.message.3"),
        "warning",
      );
      return;
    }
    if (!emailFormatValidator(changeDialogData.email)) {
      snackbar(
        t("page.analytics.sales.performance.change.function.error.message.4"),
        "warning",
      );
      return;
    }
    const result = await handleChangeSales();
    closeChangeDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  // ===================== create funciton ===================== //
  const openCreateDialog = () => {
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    restoreCreateFormInput();
    setIsCreateDialogOpen(false);
  };

  const handleCreateFormInput = (data, field) => {
    setCreateDialogData({
      ...createDialogData,
      [field]: data,
    });
  };

  const restoreCreateFormInput = () => {
    setCreateDialogData({});
  };

  const createContent = () => {
    return <CreateContent handleInput={handleCreateFormInput} />;
  };

  const handleCreateSales = async () => {
    const payload = trimObject({
      firstName: createDialogData.firstName,
    });
    const response = await createSales(payload);
    const result = response?.data;
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.create.function.success.message"), "success");
      return true;
    } else {
      snackbar(t("general.message.create.function.fail.message"), "error");
      return false;
    }
  };

  const confirmCreate = async () => {
    if (!createDialogData.firstName) {
      snackbar(
        t("page.analytics.sales.performance.create.function.error.message.1"),
        "warning",
      );
      return;
    }
    const result = await handleCreateSales();
    closeCreateDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  const handleUpdateCommission = async () => {
    if (!searchFieldInput.startDate && !searchFieldInput.endDate) {
      snackbar(t("general.message.update.function.error.message.1"), "warning");
      return;
    }
    if (searchFieldInput.startDate && !searchFieldInput.endDate) {
      snackbar(t("general.message.update.function.error.message.2"), "warning");
      return;
    }
    if (searchFieldInput.endDate && !searchFieldInput.startDate) {
      snackbar(t("general.message.update.function.error.message.3"), "warning");
      return;
    }
    if (searchFieldInput.startDate > searchFieldInput.endDate) {
      snackbar(t("general.message.update.function.error.message.4"), "warning");
      return;
    }
    const payload = {
      startDate: searchFieldInput.startDate,
      endDate: searchFieldInput.endDate,
    };
    const response = await updateCommission(payload);
    const result = response?.data;
    if (result && result.responseCode === "0000") {
      snackbar(t("general.message.update.function.success.message"), "success");
      return true;
    } else {
      snackbar(t("general.message.update.function.fail.message"), "error");
      return false;
    }
  };

  const updateBtnIcon = () => {
    return <img src={UpdateDataIcon} width={16} />;
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const columns = [
    {
      index: 0,
      key: "id",
      title: t("page.analytics.sales.performance.column.title.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "firstName",
      title: t("page.analytics.sales.performance.column.title.first.name"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "currency",
      title: t(
        "page.analytics.sales.performance.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "sum",
      title: t(
        "page.analytics.sales.performance.column.title.total.transaction.volume",
      ),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "commission",
      title: t("page.analytics.sales.performance.column.title.commission"),
      type: "amount",
      sorter: false,
      render: (data, key) => {
        return checkMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "count",
      title: t(
        "page.analytics.sales.performance.column.title.number.of.new.merchants",
      ),
      sorter: false,
      render: (data, key) => {
        return data[key];
      },
    },
    {
      index: 6,
      key: "action",
      title: t("page.analytics.sales.performance.column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS
              variant="text-only"
              onClick={() => {
                return openChangeDialog(data);
              }}
            >
              {t("page.analytics.sales.performance.column.title.action.1")}
            </ButtonMAS>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <Datepicker
            label={t(
              "page.analytics.sales.performance.transaction.start.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.sales.performance.transaction.start.date.error.message.2",
                  )
                : t(
                    "page.analytics.sales.performance.transaction.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.analytics.sales.performance.transaction.end.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.analytics.sales.performance.transaction.end.date.error.message.2",
                  )
                : t(
                    "page.analytics.sales.performance.transaction.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Selection
            label={t(
              "page.analytics.sales.performance.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>{t("page.analytics.sales.performance.remarks.1")}</span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching}
            createBtn
            handleCreate={openCreateDialog}
            customBtn
            customBtnName={t(
              "page.analytics.sales.performance.update.function.label",
            )}
            customBtnIcon={updateBtnIcon()}
            handleCustomBtn={handleUpdateCommission}
            refreshBtn
            handleRefresh={handleSearch}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference: "analytics-sales-performance",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
        <SimpleDialog
          open={isChangeDialogOpen}
          dialogOncloseFn={closeChangeDialog}
          title={t(
            "page.analytics.sales.performance.change.function.dialog.title",
          )}
          content={changeContent()}
          confirmFn={confirmChange}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
        />
        <SimpleDialog
          open={isCreateDialogOpen}
          dialogOncloseFn={closeCreateDialog}
          title={t(
            "page.analytics.sales.performance.create.function.dialog.title",
          )}
          content={createContent()}
          confirmFn={confirmCreate}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
        />
      </div>
    </div>
  );
};

export default React.memo(SalesPerformance);
